/** Small shared helpers. Deliberately dependency-free -- this app has no framework. */

/** Escapes text for safe interpolation into an innerHTML template. */
export function escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}

export function formatBytes(bytes) {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatDate(value) {
    if (!value) return '';
    const d = new Date(value.includes('T') ? value : value.replace(' ', 'T') + 'Z');
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** The design system gives each kind of toast its own icon chip; see styles/shanks-ds.css. */
const TOAST_ICONS = {
    success: 'fa-check',
    error: 'fa-triangle-exclamation',
    warning: 'fa-triangle-exclamation',
    info: 'fa-circle-info',
};

/**
 * Transient status message, in the shape the design system's toast expects: an icon chip, the
 * message, and a dismiss button. The message is set as text, never as markup -- some of these
 * carry an API error string, which is not ours to trust.
 */
export function showToast(message, type = 'info', duration = 3500) {
    // Deferred to the next frame: appending during a route render otherwise forces a
    // synchronous layout while the page is still being built.
    requestAnimationFrame(() => {
        const container = document.getElementById('toast-container');
        if (!container) return;

        const toast = document.createElement('div');
        toast.className = `toast toast-${type}`;
        toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

        const icon = document.createElement('i');
        icon.className = `toast-icon fa-solid ${TOAST_ICONS[type] ?? TOAST_ICONS.info}`;
        icon.setAttribute('aria-hidden', 'true');

        const body = document.createElement('div');
        body.className = 'toast-body';
        body.textContent = message;

        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'toast-close';
        close.setAttribute('aria-label', 'Dismiss');
        close.innerHTML = '<i class="fa-solid fa-xmark" aria-hidden="true"></i>';

        toast.append(icon, body, close);
        container.appendChild(toast);

        let removing = false;
        const dismiss = () => {
            if (removing) return;
            removing = true;
            clearTimeout(timer);
            toast.classList.add('fade-out');
            setTimeout(() => toast.remove(), 300);
        };

        close.addEventListener('click', dismiss);

        setTimeout(() => toast.classList.add('show'), 10);
        const timer = setTimeout(dismiss, Math.max(1500, duration));
    });
}

/**
 * What to upload a file as.
 *
 * A browser leaves `file.type` empty for a format the operating system does not recognise, and
 * HEIC on Windows and most Linux desktops is the usual case. The extension decides then. The API
 * refuses anything outside its allowlist and the upload gate checks the bytes, so this is a
 * label for the file, not a check on it.
 */
const TYPE_BY_EXTENSION = {
    heic: 'image/heic',
    heif: 'image/heif',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    gif: 'image/gif',
    mp4: 'video/mp4',
    m4v: 'video/mp4',
    mov: 'video/quicktime',
    webm: 'video/webm',
};

export function uploadType(file) {
    const declared = String(file?.type ?? '').toLowerCase();
    if (declared && declared !== 'application/octet-stream') return declared === 'image/jpg' ? 'image/jpeg' : declared;
    const extension = /\.([a-z0-9]+)$/i.exec(file?.name ?? '')?.[1]?.toLowerCase();
    return TYPE_BY_EXTENSION[extension] ?? declared;
}

export function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}


const RETURN_TO_KEY = 'v2.returnTo';

/**
 * Remembers where to send someone after they sign in.
 *
 * Only same-origin paths are accepted, and `//host` is rejected explicitly: the browser
 * reads a protocol-relative URL as another origin, so without that check this would be an
 * open redirect that any event link could aim at a phishing page.
 *
 * sessionStorage, so it dies with the tab rather than surprising someone on a later visit.
 */
export function setReturnTo(path) {
    if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) return;
    try {
        sessionStorage.setItem(RETURN_TO_KEY, path);
    } catch {
        // Private browsing refuses storage; the user just lands on the home page instead.
    }
}

/** Reads and clears it -- a return path is used once. */
export function takeReturnTo() {
    try {
        const path = sessionStorage.getItem(RETURN_TO_KEY);
        sessionStorage.removeItem(RETURN_TO_KEY);
        return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') ? path : null;
    } catch {
        return null;
    }
}

/** A six-digit code, anywhere in the pasted text -- people copy whole lines out of an email. */
const CODE_IN_TEXT = /\b(\d{6})\b/;

/**
 * Adds a "Paste code" button beside a security-code field, and keeps the field to digits.
 *
 * Every one of these codes arrives somewhere else -- an email, an authenticator app -- so the
 * real action is always copy, switch back, paste. On a phone that is fiddly enough that people
 * retype the digits instead.
 *
 * The clipboard is only ever read from the button's own click. Reading it on focus would work in
 * Chrome but pops a permission prompt the visitor did not ask for, and fails silently elsewhere,
 * so the button is always offered rather than conditionally shown.
 */
export function enhanceCodeInput(input) {
    if (!input || input.dataset.codeEnhanced) return;
    input.dataset.codeEnhanced = '1';
    input.setAttribute('autocomplete', 'one-time-code');
    input.setAttribute('inputmode', 'numeric');

    const limit = input.maxLength > 0 ? input.maxLength : 6;

    const row = document.createElement('div');
    row.className = 'code-input-row';
    input.parentNode.insertBefore(row, input);
    row.appendChild(input);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-secondary code-paste-btn';
    button.innerHTML = '<i class="fa-solid fa-paste"></i><span>Paste code</span>';
    row.appendChild(button);

    button.addEventListener('click', async () => {
        let text = '';
        try {
            text = (await navigator.clipboard?.readText?.()) ?? '';
        } catch {
            // Refused, or no clipboard API at all (Firefox without permission, older Safari).
            showToast('Your browser will not let the page read the clipboard — paste into the box instead', 'info');
            input.focus();
            return;
        }
        const match = CODE_IN_TEXT.exec(text) ?? /(\d{4,8})/.exec(text.replace(/\s+/g, ''));
        if (!match) {
            showToast('No code found in your clipboard', 'info');
            input.focus();
            return;
        }
        input.value = match[1].slice(0, limit);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
    });

    // Codes get copied with spaces in them ("123 456"), and an email may carry a stray newline.
    input.addEventListener('input', () => {
        const digits = input.value.replace(/\D/g, '').slice(0, limit);
        if (digits !== input.value) input.value = digits;
    });
}
